import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { PATHS } from 'src/common/constants';
import { PasswordRequirements } from 'src/SharedCustomHooks/UsePasswordRequirements/usePasswordRequirements';
import {
  ResetLinkValidation,
  useAuthFlowsAPI,
} from 'src/SharedCustomHooks/UseAuthFlowsAPI/useAuthFlowsAPI';
import { useNewPasswordFormState } from 'src/SharedCustomHooks/UseNewPasswordFormState/useNewPasswordFormState';
import { ToastFail } from 'src/helpers/validationHelper';
import { parseSearchPreservingPlus } from 'src/utils/parseSearchParams';

export type LinkPasswordFlowType = 'Reset Password' | 'Activation';

export type LinkStatus =
  | { kind: 'validating' }
  | { kind: 'valid' }
  | { kind: 'invalid'; message: string };

export interface UseLinkBasedPasswordFlowReturn {
  linkStatus: LinkStatus;
  password: string;
  confirmPassword: string;
  requirements: PasswordRequirements;
  passwordsMismatch: boolean;
  canSubmit: boolean;
  isSubmitting: boolean;
  updatePassword: (value: string) => void;
  updateConfirmPassword: (value: string) => void;
  submit: () => Promise<void>;
}

interface LinkParams {
  email: string;
  code: string;
}

const parseLinkParams = (search: string): LinkParams | null => {
  const params: URLSearchParams = parseSearchPreservingPlus(search);
  const email: string = (params.get('email') ?? '').trim();
  const code: string = (params.get('code') ?? '').trim();

  if (!email || !code) {
    return null;
  }

  return { email, code };
};

export const useLinkBasedPasswordFlow = (
  submitType: LinkPasswordFlowType
): UseLinkBasedPasswordFlowReturn => {
  const history: any = useHistory();
  const location = useLocation();

  const { validateResetUrl, submitNewPassword } = useAuthFlowsAPI();

  const {
    password,
    confirmPassword,
    requirements,
    passwordsMismatch,
    canSubmit,
    updatePassword,
    updateConfirmPassword,
  } = useNewPasswordFormState();

  const [linkStatus, setLinkStatus] = useState<LinkStatus>({ kind: 'validating' });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const initialRun = async (linkParams: LinkParams): Promise<void> => {
    const result: ResetLinkValidation = await validateResetUrl(
      linkParams.email,
      linkParams.code
    );

    if (result.status === 'valid') {
      setLinkStatus({ kind: 'valid' });
      return;
    }

    setLinkStatus({ kind: 'invalid', message: result.message });
  };

  useEffect(() => {
    const linkParams: LinkParams | null = parseLinkParams(location.search);

    if (!linkParams) {
      ToastFail('Invalid link');
      setLinkStatus({ kind: 'invalid', message: '' });
      return;
    }

    void initialRun(linkParams);
  }, []);

  const submit = async (): Promise<void> => {
    if (!canSubmit) {
      console.warn(
        `useLinkBasedPasswordFlow.submit (${submitType}) called while canSubmit is false — submit button should have been disabled.`
      );
      return;
    }

    const linkParams: LinkParams | null = parseLinkParams(location.search);

    if (!linkParams) {
      console.warn(
        `useLinkBasedPasswordFlow.submit (${submitType}) called without valid link params — link should have been marked invalid before reaching submit.`
      );
      return;
    }

    setIsSubmitting(true);

    const success: boolean = await submitNewPassword({
      email: linkParams.email,
      code: linkParams.code,
      password,
      newPassword: password,
      type: submitType,
    });

    setIsSubmitting(false);

    if (success) {
      history.push(PATHS.AUTH_SIGN_IN);
    }
  };

  return {
    linkStatus,
    password,
    confirmPassword,
    requirements,
    passwordsMismatch,
    canSubmit,
    isSubmitting,
    updatePassword,
    updateConfirmPassword,
    submit,
  };
};
